import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Chip,
  Fade,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import LockResetIcon from "@mui/icons-material/LockReset";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SecurityIcon from "@mui/icons-material/Security";
import { useNavigate, useLocation } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { authState } from "../../recoil/authAtom";
import { usePasswordExpiryNotification } from "../../hooks/usePasswordExpiryNotification";

const AUTH_ROUTES = [
  "/user-login",
  "/org-login",
  "/centre/login",
  "/forgot-password",
  "/common/verify-otp",
  "/org-register",
  "/org-verify-otp",
  "/",
];

const PasswordExpiryNotification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useRecoilValue(authState);
  const {
    expiryData,
    isModalOpen,
    openModal,
    snooze,
    currentUserId,
    currentUserName,
  } = usePasswordExpiryNotification();

  const isAuthRoute =
    AUTH_ROUTES.includes(location.pathname) ||
    location.pathname.startsWith("/common/verify-otp") ||
    location.pathname.startsWith("/forgot-password");

  if (isAuthRoute || !auth || !expiryData || !expiryData.isExpired) {
    return null;
  }

  // Parse user parts (stateCode-orgCode-userCode)
  const idParts = (currentUserId || expiryData.userId || "").split("-");
  const stateCode = idParts[0] || "";
  const orgCode = idParts[1] || "";
  const userCode = idParts[2] || "";

  const handleChangePassword = () => {
    snooze(15); // snooze popup while on change password page
    navigate("/forgot-password", {
      state: {
        userId: currentUserId || expiryData.userId || "",
        stateCode,
        orgCode,
        userCode,
      },
    });
  };

  const formattedDate = expiryData.lastPasswordChanged
    ? new Date(expiryData.lastPasswordChanged).toLocaleDateString("en-IN", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "Not recorded (Default: 1 Jan 2026)";

  return (
    <>
      {/* ───────────────── Main Expiry Modal Dialog ───────────────── */}
      <Dialog
        open={Boolean(isModalOpen)}
        onClose={() => snooze(30)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0 24px 60px rgba(15, 23, 42, 0.25)",
            overflow: "hidden",
            border: "1px solid #FEE2E2",
          },
        }}
      >
        {/* Header with Security Gradient */}
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)",
            color: "#FFFFFF",
            p: 2.5,
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              backgroundColor: "rgba(255, 255, 255, 0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <LockResetIcon sx={{ fontSize: 28, color: "#FFFFFF" }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: "1.15rem",
                lineHeight: 1.25,
                color: "#FFFFFF",
              }}
            >
              Password Change Required
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255, 255, 255, 0.85)",
                fontSize: "0.78rem",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mt: 0.3,
              }}
            >
              <SecurityIcon sx={{ fontSize: 14 }} />
              Security Policy Enforcement (180 Days Limit)
            </Typography>
          </Box>
          <IconButton
            onClick={() => snooze(30)}
            aria-label="Dismiss for 30 minutes"
            sx={{
              color: "rgba(255, 255, 255, 0.8)",
              "&:hover": {
                color: "#FFFFFF",
                backgroundColor: "rgba(255, 255, 255, 0.15)",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        {/* Modal Content */}
        <DialogContent sx={{ p: 3, backgroundColor: "#FFFFFF" }}>
          {/* Main Notice Box */}
          <Box
            sx={{
              p: 2,
              mb: 2.5,
              mt: 2.5,
              borderRadius: "10px",
              backgroundColor: "#FEF2F2",
              border: "1px solid #FECACA",
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
            }}
          >
            <WarningAmberIcon
              sx={{ color: "#DC2626", fontSize: 24, mt: 0.2, flexShrink: 0 }}
            />
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  color: "#991B1B",
                  fontWeight: 700,
                  fontSize: "0.92rem",
                  mb: 0.5,
                }}
              >
                Password Expiry Notice
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#7F1D1D", fontSize: "0.85rem", lineHeight: 1.5 }}
              >
                {expiryData.message ||
                  `Your password was last changed ${expiryData.diffDays} days ago, which exceeds our 180-day security policy. For the security of your account and organization data, you must update your password.`}
              </Typography>
            </Box>
          </Box>

          {/* Details Grid Card */}
          <Box
            sx={{
              p: 2,
              borderRadius: "10px",
              backgroundColor: "#F8FAFC",
              border: "1px solid #E2E8F0",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "#64748B",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                display: "block",
                mb: 1.5,
              }}
            >
              Account & Policy Details
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 1.5,
              }}
            >
              <Box>
                <Typography variant="caption" sx={{ color: "#64748B" }}>
                  User Name:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#1E293B" }}
                >
                  {currentUserName || expiryData.userName}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" sx={{ color: "#64748B" }}>
                  User ID:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: "#1E293B",
                    fontFamily: "monospace",
                  }}
                >
                  {currentUserId || expiryData.userId}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" sx={{ color: "#64748B" }}>
                  Last Changed Date:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#1E293B" }}
                >
                  {formattedDate}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" sx={{ color: "#64748B" }}>
                  Days Elapsed:
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mt: 0.2,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, color: "#DC2626" }}
                  >
                    {expiryData.diffDays} Days
                  </Typography>
                  <Chip
                    label="Expired (>180d)"
                    size="small"
                    sx={{
                      backgroundColor: "#FEE2E2",
                      color: "#DC2626",
                      fontWeight: 700,
                      height: 20,
                      fontSize: "0.68rem",
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        {/* Modal Actions */}
        <DialogActions
          sx={{
            p: 2.5,
            pt: 1.5,
            backgroundColor: "#F8FAFC",
            borderTop: "1px solid #E2E8F0",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button
            onClick={() => snooze(30)}
            sx={{
              color: "#64748B",
              fontWeight: 600,
              fontSize: "0.85rem",
              textTransform: "none",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              "&:hover": {
                backgroundColor: "#F1F5F9",
                color: "#334155",
              },
            }}
          >
            <AccessTimeIcon sx={{ fontSize: 16 }} />
            Remind Me in 30 Mins
          </Button>

          <Button
            variant="contained"
            onClick={handleChangePassword}
            sx={{
              background: "linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)",
              color: "#FFFFFF",
              fontWeight: 700,
              fontSize: "0.9rem",
              textTransform: "none",
              px: 3,
              py: 1,
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(220, 38, 38, 0.35)",
              display: "flex",
              alignItems: "center",
              gap: 1,
              "&:hover": {
                background: "linear-gradient(135deg, #B91C1C 0%, #991B1B 100%)",
                boxShadow: "0 6px 16px rgba(220, 38, 38, 0.45)",
              },
            }}
          >
            Change Password Now
            <ArrowForwardIcon sx={{ fontSize: 18 }} />
          </Button>
        </DialogActions>
      </Dialog>

      {/* ───────────────── Compact Floating Reminder Banner (When Snoozed) ───────────────── */}
      {!isModalOpen && (
        <Fade in={!isModalOpen}>
          <Box
            sx={{
              position: "fixed",
              bottom: 24,
              right: 24,
              zIndex: 9999,
              backgroundColor: "#FFFFFF",
              borderRadius: "12px",
              boxShadow: "0 12px 32px rgba(15, 23, 42, 0.18)",
              border: "1px solid #FECACA",
              borderLeft: "5px solid #DC2626",
              p: 1.5,
              px: 2,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              maxWidth: 420,
              animation: "fadeIn 0.3s ease",
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                backgroundColor: "#FEE2E2",
                color: "#DC2626",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <LockResetIcon sx={{ fontSize: 20 }} />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  fontSize: "0.82rem",
                  color: "#991B1B",
                  lineHeight: 1.2,
                }}
              >
                Password Expired ({expiryData.diffDays} Days Ago)
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "#64748B",
                  fontSize: "0.74rem",
                  display: "block",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                180-day policy limit exceeded.
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={openModal}
                sx={{
                  borderColor: "#DC2626",
                  color: "#DC2626",
                  fontWeight: 600,
                  fontSize: "0.72rem",
                  textTransform: "none",
                  py: 0.4,
                  px: 1,
                  minWidth: 0,
                  "&:hover": {
                    backgroundColor: "#FEF2F2",
                    borderColor: "#B91C1C",
                  },
                }}
              >
                Review
              </Button>

              <Button
                size="small"
                variant="contained"
                onClick={handleChangePassword}
                sx={{
                  backgroundColor: "#DC2626",
                  color: "#FFFFFF",
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  textTransform: "none",
                  py: 0.4,
                  px: 1.2,
                  minWidth: 0,
                  "&:hover": {
                    backgroundColor: "#B91C1C",
                  },
                }}
              >
                Update
              </Button>

              <IconButton
                size="small"
                onClick={() => snooze(60)}
                aria-label="Dismiss reminder for 1 hour"
                title="Dismiss for 1 hour"
                sx={{
                  color: "#94A3B8",
                  p: 0.4,
                  ml: 0.3,
                  "&:hover": {
                    color: "#DC2626",
                    backgroundColor: "#FEF2F2",
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          </Box>
        </Fade>
      )}
    </>
  );
};

export default PasswordExpiryNotification;
